import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, Clock, HelpCircle } from 'lucide-react';

const META = {
  compliant:  { label: 'Compliant',  Icon: CheckCircle2,  cls: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
  due_soon:   { label: 'Due soon',   Icon: Clock,         cls: 'bg-amber-50 border-amber-300 text-amber-700' },
  overdue:    { label: 'Overdue',    Icon: AlertTriangle, cls: 'bg-rose-50 border-rose-300 text-rose-700' },
  no_entries: { label: 'No entries', Icon: HelpCircle,    cls: 'bg-slate-100 border-slate-300 text-slate-600' },
};

function formatHours(h) {
  if (h == null) return '';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${Math.round(h)}h`;
  const d = h / 24;
  return `${d.toFixed(1)}d`;
}

function formatRelative(iso) {
  if (!iso) return 'No entries';
  try {
    const t = new Date(iso).getTime();
    const msAgo = Date.now() - t;
    if (msAgo < 0) return 'just now';
    const minutesAgo = msAgo / 60000;
    if (minutesAgo < 1) return 'just now';
    if (minutesAgo < 60) return `${Math.round(minutesAgo)}m ago`;
    const hoursAgo = minutesAgo / 60;
    if (hoursAgo < 24) return `${Math.round(hoursAgo)}h ago`;
    const days = hoursAgo / 24;
    return `${days.toFixed(1)}d ago`;
  } catch { return 'Unknown'; }
}

export function BowelChartComplianceBadge({ compliance, size = 'sm' }) {
  if (!compliance) return null;
  const meta = META[compliance.status] || META.no_entries;
  const Icon = meta.Icon;
  const textSize = size === 'lg' ? 'text-xs' : 'text-[10px]';
  const iconSize = size === 'lg' ? 'w-3 h-3' : 'w-2.5 h-2.5';
  return (
    <Badge
      variant="outline"
      className={`${textSize} ${meta.cls}`}
      data-testid={`compliance-badge-${compliance.status}`}
      title={compliance.last_entry_at
        ? `Last entry ${formatRelative(compliance.last_entry_at)}`
        : 'No entries recorded yet'}
    >
      <Icon className={`${iconSize} mr-1`} />{meta.label}
      {compliance.status === 'overdue' && compliance.overdue_by_hours != null && (
        <span className="ml-1 opacity-80">· {formatHours(compliance.overdue_by_hours)} over</span>
      )}
    </Badge>
  );
}

export function BowelChartComplianceSummary({ compliance }) {
  if (!compliance) return null;
  const meta = META[compliance.status] || META.no_entries;
  return (
    <div className="flex flex-col gap-0.5 text-xs" data-testid="compliance-summary-block">
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Compliance:</span>
        <BowelChartComplianceBadge compliance={compliance} size="lg" />
      </div>
      {compliance.last_entry_at ? (
        <div className="text-muted-foreground">
          Last entry <span className="font-medium">{formatRelative(compliance.last_entry_at)}</span>
          {compliance.status === 'overdue' && compliance.overdue_by_hours != null && (
            <> · <span className="text-rose-700 font-medium">overdue by {formatHours(compliance.overdue_by_hours)}</span></>
          )}
          {compliance.recent_active_entry_count_7d != null && (
            <> · <span className="font-medium">{compliance.recent_active_entry_count_7d}</span> in last 7d</>
          )}
        </div>
      ) : (
        <div className="text-muted-foreground">{meta.label === 'No entries' ? 'No entries recorded yet.' : ''}</div>
      )}
    </div>
  );
}
